import { Component, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { take } from 'rxjs';
import { Auth } from '../../services/auth';
import { EntitlementService } from '../../services/entitlement.service';
import { UserService } from '../../services/user';

@Component({
  selector: 'app-claim-account-dialog',
  imports: [
    ReactiveFormsModule,
    InputGroupModule,
    InputIconModule,
    IconFieldModule,
    InputGroupAddonModule,
    InputTextModule,
    ButtonModule,
    FloatLabelModule,
    CheckboxModule,
  ],
  templateUrl: './claim-account-dialog.html',
  styleUrl: './claim-account-dialog.css',
  standalone: true,
})
export class ClaimAccountDialog {
  private authService = inject(Auth);
  private entitlementService = inject(EntitlementService);
  private userService = inject(UserService);
  private dialogRef = inject(DynamicDialogRef);
  private messageService = inject(MessageService);

  currentPasswordVisible = false;
  newPasswordVisible = false;
  confirmNewPasswordVisible = false;
  isLoading = signal(false);
  isEditingEmail = signal(false);
  private originalEmail = '';

  form = new FormGroup(
    {
      email: new FormControl('', [Validators.required, Validators.email]),
      emailConfirmed: new FormControl(false, [Validators.requiredTrue]),
      oldPassword: new FormControl('', [Validators.required]),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
      ]),
      confirmNewPassword: new FormControl('', [Validators.required]),
    },
    { validators: ClaimAccountDialog.passwordsMatchValidator },
  );

  constructor() {
    const email = this.userService.currentUser()?.email ?? '';
    this.originalEmail = email;
    this.form.controls.email.setValue(email);
    this.form.controls.email.disable();
  }

  private static passwordsMatchValidator(
    group: AbstractControl,
  ): ValidationErrors | null {
    if (!(group instanceof FormGroup)) {
      return null;
    }

    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmNewPassword')?.value;
    if (password !== confirmPassword) {
      return { passwordsMismatch: true };
    }

    return null;
  }

  startEmailEdit(): void {
    this.isEditingEmail.set(true);
    this.form.controls.email.enable();
    this.form.controls.emailConfirmed.clearValidators();
    this.form.controls.emailConfirmed.setValue(false);
    this.form.controls.emailConfirmed.updateValueAndValidity();
  }

  cancelEmailEdit(): void {
    this.isEditingEmail.set(false);
    this.form.controls.email.setValue(this.originalEmail);
    this.form.controls.email.disable();
    this.form.controls.emailConfirmed.setValidators([Validators.requiredTrue]);
    this.form.controls.emailConfirmed.setValue(false);
    this.form.controls.emailConfirmed.updateValueAndValidity();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      // show different message if email is not confirmed
      if (!this.form.controls.emailConfirmed.value) {
        this.messageService.add({
          severity: 'error',
          summary: $localize`Error`,
          detail: $localize`Please confirm your email address.`,
        });
        return;
      }

      this.messageService.add({
        severity: 'error',
        summary: $localize`Error`,
        detail: $localize`Please fill in all required fields.`,
      });
      return;
    }

    const { oldPassword, newPassword, email, emailConfirmed } =
      this.form.getRawValue();

    if ((oldPassword ?? '') === (newPassword ?? '')) {
      this.messageService.add({
        severity: 'error',
        summary: $localize`Error`,
        detail: $localize`New password must be different from the current password.`,
      });
      return;
    }

    this.isLoading.set(true);
    this.authService
      .claimAccount({
        oldPassword: oldPassword ?? '',
        newPassword: newPassword ?? '',
        email: email ?? '',
        emailConfirmed: this.isEditingEmail()
          ? false
          : (emailConfirmed ?? false),
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.entitlementService
            .refreshEntitlements()
            .pipe(take(1))
            .subscribe();
          this.userService.refreshCurrentUser().pipe(take(1)).subscribe();
          this.messageService.add({
            severity: 'success',
            summary: $localize`Success`,
            detail: $localize`Account claimed successfully`,
          });
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.isLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: $localize`Error`,
            detail:
              err?.error?.message ??
              $localize`Could not claim account. Please try again.`,
          });
        },
      });
  }

  toggleCurrentPasswordVisibility(): void {
    this.currentPasswordVisible = !this.currentPasswordVisible;
  }

  toggleNewPasswordVisibility(): void {
    this.newPasswordVisible = !this.newPasswordVisible;
  }

  toggleConfirmNewPasswordVisibility(): void {
    this.confirmNewPasswordVisible = !this.confirmNewPasswordVisible;
  }
}
